import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Send, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  interviewInvitationsApi,
  InterviewInvitation,
  InterviewInvitationMessage,
  OtherPartyIdentity,
} from '../services/interviewInvitationsApi';
import './InterviewThread.scss';

/**
 * Shared interview-invitation thread page.
 *
 * Used by BOTH parties:
 *   - Employee at /pasiulymai/:id
 *   - Employer at /employer/pokalbiai/:id
 *
 * The component figures out the role from the API response so the same
 * code path renders either side of the conversation correctly. The
 * composer is locked until status === 'accepted'.
 *
 * Polling every 15s keeps the thread reasonably fresh without WS overhead.
 */
const POLL_INTERVAL_MS = 15_000;

function otherPartyDisplay(p: OtherPartyIdentity | null): string {
  if (!p) return 'Pašnekovas';
  if (p.company_name && p.company_name.trim()) return p.company_name.trim();
  const personal = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
  if (personal) return personal;
  return p.email ?? 'Pašnekovas';
}

const STATUS_HINT: Record<string, string> = {
  pending:
    'Kvietimas dar nepatvirtintas — kai kita pusė patvirtins, galėsite rašyti viena kitam.',
  accepted: '',
  declined: 'Šis kvietimas atmestas. Pokalbis nebevyksta.',
  withdrawn: 'Darbdavys atšaukė kvietimą. Pokalbis nebevyksta.',
};

const InterviewThread: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { id: rawId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const invitationId = rawId ? parseInt(rawId, 10) : NaN;

  const [invitation, setInvitation] = useState<InterviewInvitation | null>(null);
  const [otherParty, setOtherParty] = useState<OtherPartyIdentity | null>(null);
  const [role, setRole] = useState<'employer' | 'candidate' | null>(null);
  const [messages, setMessages] = useState<InterviewInvitationMessage[]>([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (Number.isNaN(invitationId)) return;
    try {
      const [meta, threadResp] = await Promise.all([
        interviewInvitationsApi.getById(invitationId),
        interviewInvitationsApi.messages(invitationId),
      ]);
      setInvitation(meta.invitation);
      setOtherParty(meta.other_party);
      setRole(meta.role);
      setMessages(threadResp.messages);
      setError(null);
      // The GET marks the other side's messages as read; bump the nav badge.
      window.dispatchEvent(new CustomEvent('interview-invitations-changed'));
    } catch (err: any) {
      setError(err?.message || 'Nepavyko užkrauti pokalbio');
    } finally {
      setLoading(false);
    }
  }, [invitationId]);

  useEffect(() => {
    if (!isAuthenticated) return;
    refresh();
    const t = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [isAuthenticated, refresh]);

  // Auto-scroll to the bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (isLoading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    try {
      await interviewInvitationsApi.postMessage(invitationId, trimmed);
      setBody('');
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Nepavyko išsiųsti žinutės');
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      send();
    }
  };

  const meId = user?.id;
  const canPost = invitation?.status === 'accepted';
  const statusHint = invitation ? STATUS_HINT[invitation.status] : '';
  const headerName = otherPartyDisplay(otherParty);
  const composerPlaceholder = canPost
    ? 'Parašykite žinutę... (Ctrl/⌘+Enter siųsti)'
    : 'Susirašinėjimas dar neaktyvuotas';

  // The list page is the natural "back" target for either side of the
  // conversation. Falling back to `navigate(-1)` only works when the user
  // arrived here from inside the SPA — open the page from an email link,
  // a fresh tab, or after a hard reload and the browser history is empty,
  // which leaves the back button doing nothing visible. Routing by role
  // keeps the action predictable in every entry path.
  const handleBack = () => {
    if (role === 'employer') {
      navigate('/employer/pokalbiai');
    } else if (role === 'candidate') {
      navigate('/pasiulymai');
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="interview-thread-page freelance-thread-page">
      <button className="freelance-back-link" type="button" onClick={handleBack}>
        <ChevronLeft size={16} /> Atgal
      </button>

      <div className="interview-thread-headline">
        <h1 className="freelance-thread-title">{headerName}</h1>
        {invitation && (
          <span
            className={`interview-thread-status interview-thread-status--${invitation.status}`}
          >
            {invitation.status === 'pending' && 'Laukiama patvirtinimo'}
            {invitation.status === 'accepted' && 'Aktyvus'}
            {invitation.status === 'declined' && 'Atmestas'}
            {invitation.status === 'withdrawn' && 'Atšauktas'}
          </span>
        )}
      </div>

      {role === 'candidate' && invitation?.intro_message && (
        <div className="interview-thread-intro">
          <h3>Pradinė žinutė</h3>
          <p>{invitation.intro_message}</p>
        </div>
      )}

      {statusHint && (
        <div className="freelance-alert freelance-alert--error">
          <AlertTriangle size={16} /> {statusHint}
        </div>
      )}

      {error && (
        <div className="freelance-alert freelance-alert--error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <div className="freelance-thread-window" ref={scrollRef}>
        {loading ? (
          <div className="freelance-page-loading">Kraunama...</div>
        ) : messages.length === 0 ? (
          <p className="freelance-empty-hint">
            {canPost
              ? 'Pradėkite pokalbį parašydami žinutę žemiau.'
              : 'Žinutės atsiras kai pokalbis bus aktyvuotas.'}
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.from_user_id === meId;
            return (
              <div
                key={m.id}
                className={`freelance-msg ${
                  mine ? 'freelance-msg--mine' : 'freelance-msg--theirs'
                }`}
              >
                <div className="freelance-msg-bubble">{m.body}</div>
                <div className="freelance-msg-time">
                  {new Date(m.created_at).toLocaleString('lt-LT')}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="freelance-thread-composer">
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKey}
          placeholder={composerPlaceholder}
          maxLength={5000}
          disabled={!canPost || sending}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canPost || sending || !body.trim()}
          onClick={send}
        >
          <Send size={14} /> Siųsti
        </button>
      </div>
    </div>
  );
};

export default InterviewThread;
